import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Chip, Skeleton } from "../components/ui";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { getLang, setLang, useI18n } from "../services/i18n";
import content from "../services/api-content";
import "../styles/content.css";

/** ค่าเริ่มต้นของการตั้งค่า (เก็บใน me.preferences และสำเนาที่ localStorage "aitalk:prefs" ให้หน้าแชทอ่านได้) */
export const DEFAULT_PREFS = {
  language: "th", chatMode: "normal", fontSize: 16, bubbleOpacity: 100, showMsgActions: true, relationshipDefault: false,
  notifyNewCharacter: true, notifyComment: true, notifyFollower: true, notifySystem: true, aiRewrite: false,
};
export const PREFS_KEY = "aitalk:prefs";

function Row({ label, hint, children }) {
  return <div className="setting-row"><div className="grow"><div className="strong">{label}</div>{hint ? <div className="muted" style={{ fontSize: 13 }}>{hint}</div> : null}</div>{children}</div>;
}
function Switch({ checked, onChange, label }) {
  return <span className="switch"><input type="checkbox" role="switch" aria-label={label} checked={!!checked} onChange={(e) => onChange(e.target.checked)} /><i /></span>;
}

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useI18n();
  const [prefs, setPrefs] = useState({ ...DEFAULT_PREFS, language: getLang() });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const me = useAsync(() => content.me.get(), []);
  useEffect(() => {
    if (me.data) setPrefs((p) => ({ ...DEFAULT_PREFS, ...me.data.preferences, language: me.data.preferences?.language || getLang() || p.language }));
  }, [me.data]);
  const set = (patch) => { setPrefs((p) => ({ ...p, ...patch })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      await content.me.savePrefs(prefs);
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
      setLang(prefs.language);
      setDirty(false);
      toast(prefs.language === "en" ? "Settings saved" : "บันทึกการตั้งค่าแล้ว", "success");
    } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };
  const out = async () => { await logout(); navigate("/login", { replace: true }); };

  const previewStyle = { fontSize: Number(prefs.fontSize), opacity: Number(prefs.bubbleOpacity) / 100 };
  if (me.loading) return <div style={{ maxWidth: 720, margin: "0 auto" }}><Skeleton height={300} radius={16} /></div>;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <div className="page-head"><div><h1>{t("ตั้งค่า")}</h1><p>ภาษา การแสดงผลแชท และการแจ้งเตือน</p></div></div>
      {me.error ? <div className="error-text mb-16" role="alert">โหลดการตั้งค่าเดิมไม่สำเร็จ: {me.error}</div> : null}

      <Card className="mb-16">
        <h3 className="mb-8">{t("ภาษา")}</h3>
        <div className="chip-row" role="radiogroup" aria-label="ภาษา">
          {[["th", "ไทย"], ["en", "English"]].map(([v, l]) => <Chip key={v} active={prefs.language === v} onClick={() => set({ language: v })}>{l}</Chip>)}
        </div>
        <p className="faint mt-8" style={{ fontSize: 12 }}>ภาษาของหน้าจอหลัก (ข้อความที่ผู้ใช้เขียนจะไม่ถูกแปล)</p>
      </Card>

      <Card className="mb-16">
        <h3 className="mb-8">{t("การแสดงผลแชท")}</h3>
        <Row label="โหมดแสดงผล">
          <div className="chip-row" role="radiogroup" aria-label="โหมดแสดงผลแชท">{[["normal", t("ปกติ")], ["novel", t("นวนิยาย")], ["blog", t("บล็อก")]].map(([v, l]) => <Chip key={v} size="sm" active={prefs.chatMode === v} onClick={() => set({ chatMode: v })}>{l}</Chip>)}</div>
        </Row>
        <Row label={`${t("ขนาดตัวอักษร")}: ${prefs.fontSize}px`}><input type="range" min={12} max={22} value={prefs.fontSize} aria-label="ขนาดตัวอักษร" onChange={(e) => set({ fontSize: Number(e.target.value) })} style={{ accentColor: "var(--primary)" }} /></Row>
        <Row label={`${t("ความโปร่งใสบอลลูน")}: ${prefs.bubbleOpacity}%`}><input type="range" min={30} max={100} step={5} value={prefs.bubbleOpacity} aria-label="ความโปร่งใสบอลลูน" onChange={(e) => set({ bubbleOpacity: Number(e.target.value) })} style={{ accentColor: "var(--primary)" }} /></Row>
        <Row label="แสดงปุ่มตัวเลือกใต้ข้อความ"><Switch label="แสดงปุ่มตัวเลือกใต้ข้อความ" checked={prefs.showMsgActions} onChange={(v) => set({ showMsgActions: v })} /></Row>
        <Row label="เปิด Relationship Mode เป็นค่าเริ่มต้น" hint="ใช้กับแชทใหม่"><Switch label="Relationship Mode" checked={prefs.relationshipDefault} onChange={(v) => set({ relationshipDefault: v })} /></Row>
        <div className="mt-16"><div className="faint" style={{ fontSize: 12 }}>ตัวอย่าง</div>
          <div className="msg msg--char mt-8" style={previewStyle}><div className="msg__bubble" style={prefs.chatMode === "blog" ? { borderRadius: 8 } : undefined}>*ยิ้มทักทาย* "สวัสดี วันนี้เป็นยังไงบ้าง"</div></div></div>
      </Card>

      <Card className="mb-16">
        <h3 className="mb-8">{t("แจ้งเตือน")}</h3>
        <Row label="ตัวละครใหม่จากครีเอเตอร์ที่ติดตาม" hint="ป๊อปอัปบนหน้าแรก"><Switch label="ตัวละครใหม่" checked={prefs.notifyNewCharacter} onChange={(v) => set({ notifyNewCharacter: v })} /></Row>
        <Row label="ความคิดเห็นใหม่"><Switch label="ความคิดเห็นใหม่" checked={prefs.notifyComment} onChange={(v) => set({ notifyComment: v })} /></Row>
        <Row label="ผู้ติดตามใหม่"><Switch label="ผู้ติดตามใหม่" checked={prefs.notifyFollower} onChange={(v) => set({ notifyFollower: v })} /></Row>
        <Row label="ประกาศจากระบบ"><Switch label="ประกาศจากระบบ" checked={prefs.notifySystem} onChange={(v) => set({ notifySystem: v })} /></Row>
      </Card>

      <div className="row wrap">
        <Button variant="primary" onClick={save} disabled={busy || !dirty}>{busy ? "..." : t("บันทึกการตั้งค่า")}</Button>
        <Link className="btn" to="/terms">ข้อกำหนดและความเป็นส่วนตัว</Link>
        <div className="grow" />
        <Button variant="danger" onClick={out}>{t("ออกจากระบบ")}</Button>
      </div>
    </div>
  );
}
